import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import {
  HiXMark,
  HiChatBubbleLeftRight,
  HiUser,
  HiCalendar,
  HiPencil,
  HiTrash,
  HiCheck,
  HiPlus,
  HiExclamationTriangle,
} from 'react-icons/hi2';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';

const ClientComments = ({ client, currentUser, userRole }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    commentId: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (client?.id) {
      fetchComments();
    }
  }, [client?.id]);

  const fetchComments = async () => {
    setLoading(true);
    setError('');

    try {
      const commentsRef = collection(firestore, 'clients', client.id, 'comments');
      const q = query(commentsRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);

      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp),
      }));

      setComments(commentsData);
    } catch (err) {
      setError('Failed to load comments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async e => {
    e.preventDefault();

    if (!newComment.trim()) {
      setError('Please enter a comment');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const commentData = {
        content: newComment.trim(),
        authorEmail: currentUser.email,
        authorName:
          currentUser.displayName ||
          `${currentUser.firstname || ''} ${currentUser.lastname || ''}`.trim() ||
          'Unknown',
        authorRole: userRole,
        timestamp: serverTimestamp(),
        clientId: client.id,
        clientName: `${client.firstname || ''} ${client.lastname || ''}`.trim(),
      };

      const commentsRef = collection(firestore, 'clients', client.id, 'comments');
      await addDoc(commentsRef, commentData);

      setNewComment('');
      setShowAddForm(false);
      fetchComments();
    } catch (err) {
      setError('Failed to add comment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditComment = async commentId => {
    if (!editContent.trim()) {
      setError('Please enter comment content');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const commentRef = doc(firestore, 'clients', client.id, 'comments', commentId);
      await updateDoc(commentRef, {
        content: editContent.trim(),
        editedAt: serverTimestamp(),
        editedBy: currentUser.email,
      });

      setEditingComment(null);
      setEditContent('');
      fetchComments();
    } catch (err) {
      setError('Failed to update comment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = commentId => {
    setDeleteConfirm({
      isOpen: true,
      commentId,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.commentId) return;

    setIsDeleting(true);
    setError('');

    try {
      const commentRef = doc(firestore, 'clients', client.id, 'comments', deleteConfirm.commentId);
      await deleteDoc(commentRef);

      setDeleteConfirm({ isOpen: false, commentId: null });
      fetchComments();
    } catch (err) {
      setError(`Failed to delete comment: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, commentId: null });
  };

  const startEditing = comment => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  const cancelEditing = () => {
    setEditingComment(null);
    setEditContent('');
  };

  const formatDate = date => {
    if (!date) return 'Unknown date';
    try {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const canEditComment = comment => {
    return comment.authorEmail === currentUser.email || ['admin', 'moderator'].includes(userRole);
  };

  const canDeleteComment = comment => {
    return comment.authorEmail === currentUser.email || ['admin', 'moderator'].includes(userRole);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-base font-semibold text-gray-900">
          <HiChatBubbleLeftRight className="h-5 w-5 text-blue-600" />
          Comments ({comments.length})
        </h4>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          <HiPlus className="h-4 w-4" />
          Add Comment
        </button>
      </div>

      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <form
              onSubmit={handleAddComment}
              className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 md:p-4"
            >
              <div>
                <label className="mb-2 block text-xs font-medium text-gray-700 md:text-sm">
                  Your Comment
                </label>
                <textarea
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Write your comment here..."
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={loading || !newComment.trim()}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <HiPlus className="h-4 w-4" />
                      Add Comment
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-2">
            <HiExclamationTriangle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading && comments.length === 0 ? (
          <div className="py-4 text-center">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
            <p className="mt-2 text-sm text-gray-600">Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="py-8 text-center">
            <HiChatBubbleLeftRight className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          comments.map(comment => (
            <motion.div
              key={comment.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
                    <HiUser className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-black">
                      {comment.authorName || comment.authorEmail}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{comment.authorRole}</span>
                      <span>•</span>
                      <span>{formatDate(comment.timestamp)}</span>
                      {comment.editedAt && (
                        <>
                          <span>•</span>
                          <span>edited</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {canEditComment(comment) && (
                    <button
                      onClick={() => startEditing(comment)}
                      className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                      title="Edit comment"
                    >
                      <HiPencil className="h-3 w-3" />
                    </button>
                  )}
                  {canDeleteComment(comment) && (
                    <button
                      onClick={() => handleDeleteClick(comment.id)}
                      className="rounded p-1 text-gray-400 transition-colors hover:bg-red-100 hover:text-red-600"
                      title="Delete comment"
                    >
                      <HiTrash className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              {editingComment === comment.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditComment(comment.id)}
                      disabled={loading}
                      className="flex items-center gap-1 rounded-lg bg-blue-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <HiCheck className="h-3 w-3" />
                      Save
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="whitespace-pre-wrap text-sm text-gray-700">{comment.content}</p>
              )}
            </motion.div>
          ))
        )}
      </div>

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ClientComments;
